<template>
  <div class="general-tasking-section">
    <div class="section-header">
      <div class="section-title">GENERAL TASKS FOR CTR PROTOCOL :</div>
    </div>

    <div class="tasking-content">
      <!-- Tasks to meter table -->
      <div class="tasks-table-container">
        <h4 class="table-title">Tasks to meter:</h4>
        <div class="table-wrapper">
          <table class="tasks-table">
            <thead>
            <tr>
              <th>Task</th>
              <th>Repetitive</th>
              <th>Action</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="task in radioTasks" :key="task.id" class="task-row">
              <td class="task-name">{{ truncateHexValue(task.taskInfo || task.name) }}</td>
              <td class="task-repetitive">
                <span class="repetitive-badge" :class="{ 'is-repetitive': task.isRepetitive }">
                  {{ task.isRepetitive ? 'Yes' : 'No' }}
                </span>
              </td>
              <td class="task-action">
                <button @click="deleteTask(task)" class="delete-button">Delete</button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Add Task Section -->
      <div class="add-task-container">
        <h5 class="add-task-title">Add Task:</h5>

        <!-- Task Selection -->
        <div class="task-input-group">
          <label class="input-label">SELECT TASK :</label>
          <select class="task-select" v-model="selectedTask" @change="checkForRequestArchive">
            <option value="">Select a task...</option>
            <option v-for="task in generalTasks" :key="task.id" :value="task.tasks">{{ task.tasks }}</option>
          </select>
        </div>

        <!-- Repetitive Task Option for specific tasks -->
        <div v-if="shouldShowRepetitiveOption" class="task-input-group">
          <label class="input-label">REPETITIVE TASK :</label>
          <div class="repetitive-options">
            <label class="radio-option">
              <input type="radio" v-model="isRepetitive" :value="false" />
              <span class="radio-label">One-time</span>
            </label>
            <label class="radio-option">
              <input type="radio" v-model="isRepetitive" :value="true" />
              <span class="radio-label">Repetitive</span>
            </label>
          </div>
        </div>

        <!-- Request Archive specific inputs -->
        <div v-if="isRequestArchive" class="conditional-inputs">
          <div class="task-input-group">
            <label class="input-label">DATE :</label>
            <div class="date-input-wrapper">
              <input type="date" class="task-input" v-model="taskDate" />
              <div class="date-shortcuts">
              </div>
            </div>
          </div>

          <div class="task-input-group">
            <label class="input-label">FREQUENCY :</label>
            <select class="task-select" v-model="dailyHourly" @change="onFrequencyChange">
              <option value="1">Hourly</option>
              <option value="2">Daily</option>
            </select>
          </div>

          <div class="task-input-group">
            <label class="input-label">OID PARAMETER :</label>
            <select class="task-select" v-model="selectedOID">
              <option v-for="option in oidOptions" :key="option.value" :value="option.value">{{ option.name }}</option>
            </select>
          </div>

          <!-- Enhanced Format Toggle -->
          <div class="task-input-group">
            <label class="input-label">FORMAT :</label>
            <div class="repetitive-options">
              <label class="radio-option">
                <input type="radio" v-model="useEnhancedFormat" :value="false" />
                <span class="radio-label">Legacy</span>
              </label>
              <label class="radio-option">
                <input type="radio" v-model="useEnhancedFormat" :value="true" />
                <span class="radio-label">Enhanced</span>
              </label>
            </div>
          </div>

          <!-- Enhanced Format Additional Fields -->
          <div v-if="useEnhancedFormat" class="enhanced-fields">
            <div class="task-input-group">
              <label class="input-label">INDEX WITHIN DATE :</label>
              <input
                  type="number"
                  class="task-input"
                  v-model.number="indexWithinDate"
                  min="0"
                  :max="dailyHourly == 1 ? 95 : 23"
                  placeholder="Start index"
              />
              <small class="input-help">
                {{ dailyHourly == 1 ? 'For hourly: 0-95 (15-min intervals in 24h)' : 'For daily: 0-23 (hours in day)' }}
              </small>
            </div>

            <div class="task-input-group">
              <label class="input-label">REQUESTED ENTRIES :</label>
              <input
                  type="number"
                  class="task-input"
                  v-model.number="requestedEntries"
                  min="1"
                  max="255"
                  :placeholder="`Default: ${defaultRequestedEntries}`"
              />
              <small class="input-help">
                Number of data points to retrieve (1-255)
              </small>
            </div>
          </div>

          <div class="bottom-date-buttons">
            <button @click="setTodayDate" class="today-button">From Today</button>
            <button @click="setYesterdayDate" class="yesterday-button">From Yesterday</button>
            <button @click="setYesterdayPlusOneDate" class="yesterdayplusone-button">From Yesterday plus one</button>
          </div>
        </div>

        <!-- IP Port Address Setup -->
        <div v-if="selectedTask === 'ip_port_address_setup'" class="conditional-inputs">
          <div class="task-input-group">
            <label class="input-label">IP ADDRESS :</label>
            <input type="text" class="task-input" v-model="ipAddress" placeholder="Enter IP Address" />
          </div>
          <div class="task-input-group">
            <label class="input-label">PORT :</label>
            <input type="number" class="task-input" v-model="port" placeholder="Enter Port" />
          </div>
        </div>

        <!-- Custom Raw Hex -->
        <div v-if="selectedTask === 'custom_raw_in_hex'" class="conditional-inputs">
          <div class="task-input-group">
            <label class="input-label">HEX INPUT (142 BYTES) :</label>
            <input type="text" class="task-input" v-model="hexInput" :maxlength="284" placeholder="Enter the hex value" />
          </div>
        </div>

        <!-- APN Setup -->
        <div v-if="selectedTask === 'apn_setup'" class="conditional-inputs">
          <div class="task-input-group">
            <label class="input-label">APN :</label>
            <input type="text" class="task-input" v-model="apn" placeholder="Enter APN" />
          </div>
          <div class="task-input-group">
            <label class="input-label">USERNAME :</label>
            <input type="text" class="task-input" v-model="apnUsername" placeholder="Enter Username" />
          </div>
          <div class="task-input-group">
            <label class="input-label">PASSWORD :</label>
            <input type="text" class="task-input" v-model="apnPassword" placeholder="Enter Password" />
          </div>
        </div>

        <!-- Period Setup -->
        <div v-if="selectedTask === 'period_setup'" class="conditional-inputs">
          <div class="task-input-group">
            <label class="input-label">DELAY (SECONDS) :</label>
            <input type="number" class="task-input" v-model.number="delay_in_seconds" placeholder="Enter Delay in seconds" />
          </div>
          <div class="task-input-group">
            <label class="input-label">RETRIES :</label>
            <input type="number" class="task-input" v-model.number="retries" placeholder="Enter number of retries" />
          </div>
          <div class="task-input-group">
            <label class="input-label">PERIOD TYPE :</label>
            <select class="task-select" v-model="periodType">
              <option value="">Select type...</option>
              <option value="interval">Interval</option>
              <option value="day">Day</option>
            </select>
          </div>
          <div class="task-input-group" v-if="periodType === 'interval'">
            <label class="input-label">INTERVAL (1-15) :</label>
            <input type="number" min="1" max="15" class="task-input" v-model.number="intervalValue" />
          </div>
          <div class="task-input-group" v-if="periodType === 'day'">
            <label class="input-label">DAY (1-31) :</label>
            <input type="number" min="1" max="31" class="task-input" v-model.number="dayValue" />
          </div>
        </div>

        <!-- Metering Point Setup -->
        <div v-if="selectedTask === 'metering_point_setup'" class="conditional-inputs">
          <div class="task-input-group">
            <label class="input-label">METERING POINT :</label>
            <input type="text" class="task-input" v-model="meteringPoint" placeholder="Enter the metering point" />
          </div>
        </div>

        <!-- Register Request -->
        <div v-if="selectedTask === 'register_request'" class="conditional-inputs">
          <div class="task-input-group">
            <label class="input-label">REGISTER OID :</label>
            <select class="task-select" v-model="registerType">
              <option value="">Select register type...</option>
              <option value="metering_point">Metering point</option>
              <option value="apn">Apn, password and username</option>
              <option value="ip_headend">IP Address of the head-end</option>
              <option value="strategy_connection">Frequency and Strategy of the Connection</option>
            </select>
          </div>
        </div>

        <!-- CTR Firmware Update -->
        <div v-if="selectedTask === 'ctr_firmware_update'" class="conditional-inputs">
          <div class="task-input-group">
            <label class="input-label">ACTIVATION DATE :</label>
            <input type="date" v-model="activationDate" class="task-input" placeholder="Enter activation date" />
          </div>
          <div class="task-input-group">
            <label class="input-label">SF VERSION :</label>
            <select v-model="sfVersion" class="task-select">
              <option value="">Select version...</option>
              <option v-for="version in availableSFVersions" :key="version" :value="version">
                {{ version }}
              </option>
            </select>
          </div>
        </div>

        <!-- Add Task Button -->
        <div v-if="shouldShowAddTaskButton" class="add-task-button-container">
          <button @click="addTask" class="add-task-button" :disabled="isAddTaskDisabled">
            {{ loading ? 'Adding...' : 'Add Task' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "UserSettingPageGeneralTasking",
  data() {
    return {
      radioTasks: [
      ], // Updated structure: { id, name, isRepetitive, taskInfo }
      generalTasks: [
        { id: 1, tasks: "request_archive" },
        { id: 2, tasks: "custom_raw_in_hex" },
        { id: 3, tasks: "ip_port_address_setup" },
        { id: 4, tasks: "apn_setup" },
        { id: 5, tasks: "period_setup" },
        { id: 6, tasks: "metering_point_setup" },
        { id: 7, tasks: "register_request" },
        { id: 8, tasks: "sync_request" },
        { id: 9, tasks: "ctr_firmware_update" }
      ], // Mock data
      selectedTask: "",
      isRepetitive: false, // New field for repetitive tasks
      loading: false, // Loading state for API calls
      taskDate: "",
      dailyHourly: 1,
      isRequestArchive: false,
      selectedOID: "",
      hexInput: "",
      ipAddress: "",
      port: "",
      meteringPoint: "",
      apn: "",
      apnUsername: "",
      apnPassword: "",
      delay_in_seconds: 0,
      retries: 0,
      periodType: "",
      intervalValue: 0,
      dayValue: 0,
      registerType: "",
      activationDate: "",
      sfVersion: "",
      availableSFVersions: ["v1.0.0", "v1.1.0", "v2.0.0"], // Mock data

      // New properties for enhanced request_archive
      useEnhancedFormat: false,
      indexWithinDate: 0,
      requestedEntries: 24, // Default to 24
    };
  },
  computed: {
    oidOptions() {
      return [
        { name: 'OID_0203_TOTALIZER_NET_VOLUME_END_OF_DAY', value: 0x0203 },
        { name: 'OID_0233_TOTALIZER_BACKWARD_VOLUME_END_OF_DAY', value: 0x0233 },
        { name: 'OID_1813_TOTALIZER_NET_VOLUME_F2_END_OF_DAY', value: 0x1813 },
        { name: 'OID_1263_CUMULATIVE_REDUCED_DIAGNOSTIC_END_OF_DAY', value: 0x1263 },
        { name: 'OID_0163_FLOW_RATE_FORWARD_MIN_END_OF_DAY', value: 0x0163 },
        { name: 'OID_0193_FLOW_RATE_FORWARD_MAX_END_OF_DAY', value: 0x0193 },
        { name: 'OID_0200_TOTALIZER_NET_VOLUME_CURRENT', value: 0x0200 },
        { name: 'OID_0230_TOTALIZER_BACKWARD_VOLUME_CURRENT', value: 0x0230 },
        { name: 'OID_1810_TOTALIZER_NET_VOLUME_F2_CURRENT', value: 0x1810 },
        { name: 'OID_0213_TOTALIZER_GAS_VOLUME_AT_BASE_CONDITIONS_END_OF_DAY', value: 0x0213 },
        { name: 'OID_0210_TOTALIZER_GAS_VOLUME_AT_BASE_CONDITIONS_CURRENT', value: 0x0210 },
        { name: 'OID_0102_HOURLY_MEASURED_DIRECT_FLOW_RATE', value: 0x0102 },
        { name: 'OID_0122_HOURLY_CONVERTED_BACKFLOW_RATE', value: 0x0122 }
      ];
    },
    shouldShowAddTaskButton() {
      return ['custom_raw_in_hex', 'request_archive', 'register_request', 'sync_request', 'ip_port_address_setup', 'metering_point_setup', 'apn_setup', 'period_setup', 'ctr_firmware_update'].includes(this.selectedTask);
    },
    shouldShowRepetitiveOption() {
      // Show repetitive option only for these three tasks
      return ['request_archive', 'custom_raw_in_hex', 'sync_request'].includes(this.selectedTask);
    },
    isAddTaskDisabled() {
      // Disable if loading or no task selected
      return !this.selectedTask || this.loading;
    },
    defaultRequestedEntries() {
      if (this.dailyHourly == 1) { // Hourly
        return 24; // 24 hours
      } else { // Daily
        return 15; // 15 days
      }
    }
  },
  mounted() {
    // Load existing general tasks when component mounts
    this.loadGeneralTasks();
  },
  watch: {
    selectedTask() {
      // Reset repetitive option when task changes
      if (!this.shouldShowRepetitiveOption) {
        this.isRepetitive = false; // Always false for non-repetitive-allowed tasks
      }
    }
  },
  methods: {
    async loadGeneralTasks() {
      try {
        this.loading = true;
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${process.env.VUE_APP_API_URL}/tasks/general/5001`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.tasks) {
            this.radioTasks = data.tasks.map(task => ({
              id: task.id,
              name: task.name,
              isRepetitive: task.isRepetitive,
              taskInfo: task.taskInfo
            }));
          }
        }
      } catch (error) {
        // Silent error handling
      } finally {
        this.loading = false;
      }
    },
    async deleteTask(taskToDelete) {
      try {
        this.loading = true;
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${process.env.VUE_APP_API_URL}/tasks/general/5001/${taskToDelete.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          // Remove task from local array only if API call succeeds
          this.radioTasks = this.radioTasks.filter(task => task.id !== taskToDelete.id);
        }
      } catch (error) {
        // Silent error handling
      } finally {
        this.loading = false;
      }
    },
    checkForRequestArchive() {
      this.isRequestArchive = this.selectedTask === "request_archive";

      // Reset enhanced format settings when changing tasks
      if (this.isRequestArchive) {
        this.useEnhancedFormat = false;
        this.indexWithinDate = 0;
        this.requestedEntries = this.defaultRequestedEntries;
      }

      // Reset repetitive when changing tasks
      if (!this.shouldShowRepetitiveOption) {
        this.isRepetitive = false;
      }
    },
    // Watch for frequency changes to update default requested entries
    onFrequencyChange() {
      this.requestedEntries = this.defaultRequestedEntries;
    },
    setTodayDate() {
      this.taskDate = "today";
    },
    setYesterdayDate() {
      this.taskDate = "yesterday";
    },
    setYesterdayPlusOneDate() {
      this.taskDate = "yesterdayplusone";
    },
    truncateHexValue(task) {
      if (task.startsWith("custom_raw_in_hex&")) {
        return task.slice(0, "custom_raw_in_hex&".length + 20) + "...";
      }
      return task;
    },
    generateTaskId() {
      // Generate a random task ID
      return Math.random().toString(36).substr(2, 9);
    },
    async addTask() {
      if (this.selectedTask) {
        try {
          this.loading = true;

          let finalTaskName = this.selectedTask;
          let finalTaskInfo = "";
          let taskIsRepetitive = false;

          // Determine if task should be repetitive
          if (this.shouldShowRepetitiveOption) {
            taskIsRepetitive = this.isRepetitive;
          } else {
            taskIsRepetitive = false; // Always false for other tasks
          }

          // Extract task name and build task info
          if (this.selectedTask === "request_archive") {
            if (!this.taskDate || !this.selectedOID) {
              this.loading = false;
              return;
            }

            let hexOID = "0x" + parseInt(this.selectedOID).toString(16).toUpperCase().padStart(4, '0');
            finalTaskName = "request_archive";

            if (this.useEnhancedFormat) {
              // Enhanced format: request_archive&today&1&0x0102&0&24
              finalTaskInfo = `request_archive&${this.taskDate}&${this.dailyHourly}&${hexOID}&${this.indexWithinDate}&${this.requestedEntries}`;
            } else {
              // Legacy format: request_archive&today&1&0x0102
              finalTaskInfo = `request_archive&${this.taskDate}&${this.dailyHourly}&${hexOID}`;
            }
          } else if (this.selectedTask === "custom_raw_in_hex") {
            if (!this.hexInput) {
              this.loading = false;
              return;
            }
            finalTaskName = "custom_raw_in_hex";
            finalTaskInfo = `custom_raw_in_hex&${this.hexInput}`;
            let byteCount = (this.hexInput.length) / 2;
            if (byteCount !== 142) {
              this.loading = false;
              return;
            }
          } else if (this.selectedTask === "ip_port_address_setup") {
            if (!this.ipAddress || !this.port) {
              this.loading = false;
              return;
            }
            finalTaskName = "ip_port_address_setup";
            finalTaskInfo = `ip_port_address_setup&${this.ipAddress}&${this.port}`;
          } else if (this.selectedTask === "sync_request") {
            finalTaskName = "sync_request";
            finalTaskInfo = "sync_request";
          } else if (this.selectedTask === "register_request") {
            if (!this.registerType) {
              this.loading = false;
              return;
            }
            finalTaskName = "register_request";
            finalTaskInfo = `register_request&${this.registerType}`;
          } else if (this.selectedTask === "metering_point_setup") {
            if (!this.meteringPoint) {
              this.loading = false;
              return;
            }
            finalTaskName = "metering_point_setup";
            finalTaskInfo = `metering_point_setup&${this.meteringPoint}`;
          } else if (this.selectedTask === "ctr_firmware_update") {
            if (!this.activationDate || !this.sfVersion) {
              this.loading = false;
              return;
            }
            finalTaskName = "ctr_firmware_update";
            finalTaskInfo = `ctr_firmware_update&${this.activationDate}&${this.sfVersion}`;
          } else if (this.selectedTask === "period_setup") {
            if (!this.delay_in_seconds || !this.retries || !this.periodType) {
              this.loading = false;
              return;
            }

            let periodValue = 0;
            if (this.periodType === "interval") {
              if (this.intervalValue < 1 || this.intervalValue > 15) {
                this.loading = false;
                return;
              }
              periodValue = 40 + this.intervalValue;
            } else if (this.periodType === "day") {
              if (this.dayValue < 1 || this.dayValue > 31) {
                this.loading = false;
                return;
              }
              periodValue = this.dayValue;
            }

            finalTaskName = "period_setup";
            finalTaskInfo = `period_setup&${this.delay_in_seconds}&${this.retries}&${periodValue}`;
          } else if (this.selectedTask === "apn_setup") {
            if (!this.apn || !this.apnUsername || !this.apnPassword) {
              this.loading = false;
              return;
            }
            finalTaskName = "apn_setup";
            finalTaskInfo = `apn_setup&${this.apn}&${this.apnUsername}&${this.apnPassword}`;
          }

          // Check if task already exists locally (compare by name, task info and repetitive status)
          const existingTask = this.radioTasks.find(task =>
              task.name === finalTaskName &&
              task.taskInfo === finalTaskInfo &&
              task.isRepetitive === taskIsRepetitive
          );

          if (existingTask) {
            this.loading = false;
            return;
          }

          // Prepare API payload
          const taskPayload = {
            protocol: "5001", // CTR protocol
            task: {
              id: this.generateTaskId(),
              name: finalTaskName,
              isRepetitive: taskIsRepetitive,
              taskInfo: finalTaskInfo
            }
          };

          // Make API call
          const token = localStorage.getItem('access_token');

          const response = await fetch(`${process.env.VUE_APP_API_URL}/tasks/general`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(taskPayload)
          });

          if (response.ok) {
            // Add task to local array
            const newTask = {
              id: taskPayload.task.id,
              name: finalTaskName,
              isRepetitive: taskIsRepetitive,
              taskInfo: finalTaskInfo
            };

            this.radioTasks.push(newTask);

            // Reset form
            this.resetForm();
          }

        } catch (error) {
          // Silent error handling
        } finally {
          this.loading = false;
        }
      }
    },
    resetForm() {
      this.selectedTask = "";
      this.isRepetitive = false;
      this.taskDate = "";
      this.dailyHourly = 1;
      this.isRequestArchive = false;
      this.selectedOID = "";
      this.useEnhancedFormat = false; // Reset enhanced format
      this.indexWithinDate = 0;       // Reset index
      this.requestedEntries = 24;     // Reset to default
      this.hexInput = "";
      this.ipAddress = "";
      this.port = "";
      this.meteringPoint = "";
      this.apn = "";
      this.apnUsername = "";
      this.apnPassword = "";
      this.delay_in_seconds = 0;
      this.retries = 0;
      this.periodType = "";
      this.intervalValue = 0;
      this.dayValue = 0;
      this.registerType = "";
      this.activationDate = "";
      this.sfVersion = "";
    }
  }
};
</script>
<style scoped>
.general-tasking-section {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.section-header {
  margin-bottom: 20px;
  flex-shrink: 0; /* Prevent header from shrinking */
  margin-top: 80px; /* Match other sections */
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: white;
  display: inline-block;
}

.tasking-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  overflow-y: auto; /* Allow scrolling for the content */
  padding-right: 10px; /* Add some padding for scrollbar */
}

/* Tasks Table */
.tasks-table-container {
  margin-bottom: 15px;
  flex-shrink: 0; /* Prevent table from shrinking */
}

.table-title {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 15px;
}

.table-wrapper {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.tasks-table {
  width: 100%;
  border-collapse: collapse;
  background-color: transparent;
}

.tasks-table thead {
  background-color: rgba(255, 255, 255, 0.05);
}

.tasks-table th {
  padding: 12px 16px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  text-align: left;
}

.task-row {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.task-row:hover {
  background-color: rgba(255, 255, 255, 0.02);
}

.tasks-table td {
  padding: 12px 16px;
  font-size: 11px;
  color: white;
}

.task-name {
  font-family: monospace;
  font-size: 10px;
}

.task-repetitive {
  text-align: center;
}

.repetitive-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 9px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}

.repetitive-badge.is-repetitive {
  background-color: rgba(76, 175, 80, 0.2);
  color: #4caf50;
}

.delete-button {
  background: none;
  border: 1px solid rgba(244, 67, 54, 0.5);
  color: #f44336;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 9px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.2s;
}

.delete-button:hover {
  background-color: rgba(244, 67, 54, 0.1);
  border-color: #f44336;
}

/* Add Task Section */
.add-task-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex-shrink: 0; /* Prevent form from shrinking */
}

.add-task-title {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 10px;
}

.task-input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.input-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.05em;
  opacity: 0.6;
  text-transform: uppercase;
  color: white;
}

.task-input,
.task-select {
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px 0;
  font-size: 11px;
  transition: border-color 0.2s;
}

.task-input:focus,
.task-select:focus {
  outline: none;
  border-bottom-color: rgba(255, 255, 255, 0.6);
}

.task-select option {
  background-color: #2a2a2a;
  color: white;
}

/* Repetitive Options Styling */
.repetitive-options {
  display: flex;
  gap: 20px;
  margin-top: 8px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
}

.radio-option input[type="radio"] {
  accent-color: #4fc3f7;
  margin: 0;
}

.radio-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.conditional-inputs {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.02);
}

.today-button {
  background: none;
  border: 1px solid rgba(79, 195, 247, 0.5);
  color: #4fc3f7;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.2s;
  align-self: flex-start;
}

.today-button:hover {
  background-color: rgba(79, 195, 247, 0.1);
  border-color: #4fc3f7;
}

.add-task-button-container {
  margin-top: 15px;
  padding-bottom: 10px; /* Add some bottom padding */
}
.yesterday-button {
  background: none;
  border: 1px solid rgba(79, 195, 247, 0.5);
  color: #4fc3f7;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.2s;
  align-self: flex-start;
}
.yesterdayplusone-button {
  background: none;
  border: 1px solid rgba(79, 195, 247, 0.5);
  color: #4fc3f7;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.2s;
  align-self: flex-start;
}

.add-task-button {
  background: none;
  border: 1px solid rgba(76, 175, 80, 0.5);
  color: #4caf50;
  padding: 10px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.2s;
}

.add-task-button:hover:not(:disabled) {
  background-color: rgba(76, 175, 80, 0.1);
  border-color: #4caf50;
}

.add-task-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Custom scrollbar styling */
.tasking-content::-webkit-scrollbar {
  width: 6px;
}

.tasking-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.tasking-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.tasking-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Responsive adjustments */
@media (max-width: 767.98px) {
  .general-tasking-section {
    height: auto; /* Allow natural height on mobile */
  }

  .section-header {
    margin-top: 60px; /* Reduce top margin on mobile */
  }

  .tasking-content {
    gap: 15px;
    overflow-y: visible; /* Remove scroll on mobile */
    padding-right: 0;
  }

  .tasks-table th,
  .tasks-table td {
    padding: 8px 12px;
  }

  .conditional-inputs {
    padding: 12px;
  }

  .repetitive-options {
    gap: 15px;
  }
}
</style>